import * as React from 'react';
import { observer } from 'mobx-react';
import { When } from 'react-if';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';

import { DeviceConnectivity } from '@/constants/enums';
import { useStores } from '@/hooks/use-stores';
import ValveWidget from '@/comp/common/icons/ValveWidget';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { Ports } from '@/comp/MapBuilder/components/Ports';
import ConditionalToolTip from '@/utils/ConditionalTooltip';
import { CustomThemeToken } from '@/theme';

import { isSelectedNodeOnMap } from '../../utils/utils';
import { getTooltipContent, getValveRelativePosition } from './ValveNodeWidget.utils';
import { ValveContainer } from './ValveNodeWidget.styles';
import {
  getWidgetBackgroundColor,
  getWidgetCircleColor,
  getWidgetBorderColor,
} from '../../components/SensorNodeWidget/SensorNodeWidget.utils';

const { useToken } = theme;

export const ValveNodeWidget = observer(
  ({ node, engine, width, height, shouldShowTooltip }: { node; width; height; shouldShowTooltip; engine? }) => {
    const { t } = useTranslation();
    const { mapBuilderStore } = useStores();
    const { connectivity, inMaintenance } = node.getEntity();
    const { token } = useToken() as unknown as { token: CustomThemeToken };

    const onDevices = node.getOnDevices();
    const ioRelayDevice = onDevices?.find(device => device.deviceType?.includes('ioRelay'));
    const valveActivity = ioRelayDevice?.valveActivity ?? node.device.valveActivity;

    const renderedOnMap = !!engine;
    const isSelected = isSelectedNodeOnMap(
      node.device.deviceId,
      mapBuilderStore.selectedDevice?.deviceId,
      renderedOnMap,
    );
    const isConnected = inMaintenance || connectivity === DeviceConnectivity.Connected;
    const borderColor = getWidgetBorderColor(isSelected, inMaintenance, renderedOnMap, token);
    const backgroundColor = getWidgetBackgroundColor(isSelected, inMaintenance, renderedOnMap, token);
    const arrowsColor = getWidgetCircleColor(isConnected, token);

    const valveRelativePosition = getValveRelativePosition(
      valveActivity,
      node.device.valveCurrentPosition,
      node.device.deviceType,
    );

    return (
      <ConditionalToolTip
        condition={shouldShowTooltip}
        content={getTooltipContent(node.device.connectivity, valveActivity, t)}
      >
        <ValveContainer width={width} height={height}>
          <ValveWidget
            width={width}
            height={height}
            arrowsColor={arrowsColor}
            backgroundColor={backgroundColor}
            borderColor={borderColor}
          />

          <When condition={!!engine}>
            {isConnected && (
              <div className="value-container">
                {valveRelativePosition == null ? (
                  <span className="value">{t('N/A')}</span>
                ) : (
                  <div className="valve-current-position">
                    <span className="value">{valveRelativePosition}</span>
                    <span className="label">%</span>
                  </div>
                )}
              </div>
            )}

            <AlertsWidget alerts={node.alerts} />

            <Ports node={node} engine={engine} width={width} height={height} />
          </When>
        </ValveContainer>
      </ConditionalToolTip>
    );
  },
);
